import { ApiError, type Cashout } from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import { ConfirmDialog, SkeletonText } from "@khazzin/ui/primitives";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";

/**
 * The Store Portal's "Cash out now" (EP04-S08, DA-04-03): what the store's ready
 * orders come to, the express fee and the net, and one press to be paid
 * before the cadence. The Portal's Finance page (EP10-S08) mounts it.
 *
 * **One key per opening.** A double-tapped Confirm replays the first cashout
 * rather than being told there is nothing left to cash out.
 */
export function ExpressCashoutDialog({
	storeId,
	open,
	onOpenChange,
	onDone,
}: {
	storeId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onDone?: (cashout: Cashout) => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const [key] = useState(() => `express-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
	const [failed, setFailed] = useState<string | null>(null);
	const quote = useQuery({
		queryKey: ["portal", storeId, "express-quote"],
		queryFn: ({ signal }) => api.getPortalExpressCashoutQuote({ signal }),
		enabled: open,
	});
	const q = quote.data;
	const money = (amountMinor: number) =>
		formatMoney({ amountMinor, currency: q?.currency || "USD" }, locale);
	const nothing = q !== undefined && q.grossMinor <= 0;

	return (
		<ConfirmDialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("portal.finance.express.title")}
			description={t("portal.finance.express.body")}
			confirmLabel={t("portal.finance.express.confirm")}
			cancelLabel={t("ui.actions.cancel")}
			closeLabel={t("ui.actions.close")}
			onConfirm={async () => {
				setFailed(null);
				try {
					const cashout = await api.createPortalExpressCashout(key);
					await queries.invalidateQueries({ queryKey: ["portal", storeId] });
					onDone?.(cashout);
				} catch (error) {
					setFailed(error instanceof ApiError ? error.message : t("portal.finance.express.failed"));
					throw error;
				}
			}}
		>
			{quote.isLoading ? (
				<SkeletonText lines={3} />
			) : quote.isError || q === undefined ? (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{t("portal.finance.express.quoteFailed")}
				</p>
			) : nothing ? (
				<p className="text-[13px] text-fg-muted">{t("portal.finance.express.nothing")}</p>
			) : (
				<dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[13px]">
					<dt className="text-fg-muted">
						{t("portal.finance.express.gross", { count: q.orderCount })}
					</dt>
					<dd className="text-end [font-variant-numeric:tabular-nums]">{money(q.grossMinor)}</dd>
					<dt className="text-fg-muted">
						{t("portal.finance.express.fee", { percent: (q.feeBp / 100).toFixed(2) })}
					</dt>
					<dd className="text-end [font-variant-numeric:tabular-nums]">−{money(q.feeMinor)}</dd>
					<dt className="font-bold text-fg">{t("portal.finance.express.net")}</dt>
					<dd className="text-end font-bold [font-variant-numeric:tabular-nums]">
						{money(q.netMinor)}
					</dd>
				</dl>
			)}
			{failed !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failed}
				</p>
			)}
		</ConfirmDialog>
	);
}
